import React, { useState } from 'react';
import { StarIcon, XIcon } from 'lucide-react';
import { AuthorSignature } from '../components/AuthorSignature';
type ModalType = 'terms' | 'privacy' | 'contact' | null;
export function NoahsWeekendPage() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <div className="min-h-screen w-full bg-[#f8f7f4] font-sans text-[#1e293b] selection:bg-[#b91c1c] selection:text-white" data-id="element-1074">
      {/* 1. Top Nav Bar */}
      <header className="bg-[#002868] text-white w-full" data-id="element-1075">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-12 flex items-center justify-between" data-id="element-1076">
          <div className="flex items-center gap-2 font-bold text-lg tracking-tight" data-id="element-1077">
            <StarIcon className="w-5 h-5 fill-current text-[#b91c1c]" data-id="element-1078" />
            <span data-id="element-1079">PatriotsInformer</span>
          </div>
        </div>
      </header>

      {/* 2. Breadcrumb Bar */}
      <div className="bg-white border-b border-gray-200 w-full" data-id="element-1080"></div>

      {/* Main Content Area */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-12" style={{
      paddingTop: '2px'
    }} data-id="element-1081">
        {/* 3. Article Section */}
        <article className="space-y-6" data-id="element-1082">
          {/* Title & Meta */}
          <div className="space-y-2" data-id="element-1083">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#0f172a] tracking-tight leading-tight" data-id="element-1084">
              Noah's Weekend: Why You Should Stop Buying Preparedness and Start
              Building It
            </h1>
          </div>

          {/* Author Bio */}
          <div className="flex flex-row items-center gap-4 sm:gap-6" data-id="element-1085">
            <img src="https://cdn.magicpatterns.com/uploads/qCiNdfGtT6ARUfiGnS7VJm/jack-turner-avatar-2.jpg" alt="Jack Turner" className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-gray-200 shadow-sm flex-shrink-0" data-id="element-1086" />
            <div className="text-left space-y-1" data-id="element-1087">
              <h3 className="font-bold text-[#0f172a] text-lg" data-id="element-1088">Jack Turner</h3>
              <p className="text-gray-500 text-sm" data-id="element-1089">
                Head of research at PatriotsInformer.com
              </p>
              <p className="text-gray-400 text-xs uppercase tracking-wide pt-1" data-id="element-1090">
                May 5, 2026 · 4 min read
              </p>
            </div>
          </div>

          {/* Article Body */}
          <div className="space-y-6 text-lg text-[#334155] leading-relaxed" data-id="element-1091">
            <p data-id="element-1092">
              Genesis 6 says Noah was 500 years old when the Lord told him to
              build the ark. The flood came when he was 600.
            </p>
            <p data-id="element-1093">
              A hundred years of weekends. A hundred years of his neighbors
              walking past, asking what he was doing, hearing the answer,
              laughing, and walking on. A hundred years of his sons growing up
              watching their father pick up tools every Saturday morning,
              ark-shaped and a little more finished each week.
            </p>
            <p data-id="element-1094">
              Most of us don't have a hundred years. What we have is the
              weekends between this one and whenever the next regional water
              emergency hits the news cycle.{' '}
              <b data-id="element-1095">That timeline is not getting longer.</b>
            </p>

            <h2 className="text-2xl sm:text-3xl font-bold text-[#0f172a] pt-4" data-id="element-1096">
              The lesson most prepper content gets backwards
            </h2>

            <p data-id="element-1097">
              The Lord didn't tell Noah where to find the best ark on Amazon. He
              didn't send him to a vendor. He told him to <b data-id="element-1098">build one</b>. With
              his sons. With his hands. With materials he sourced and shaped
              himself.
            </p>
            <p data-id="element-1099">
              A man who buys a $400 commercial filter and puts it on a shelf has
              bought a fragile thing. If it breaks, he calls customer service.
              If the company goes out of business, he buys another brand. If the
              supply chain is interrupted, he is stranded.
            </p>
            <p data-id="element-1100">
              A man who builds the same filter has bought something different.
              He has bought <b data-id="element-1101">understanding</b>. He can fix what he built. He
              can scale it. He can teach it. If a friend's family is in trouble,
              he can build them one too.
            </p>

            <blockquote className="border-l-4 border-[#b91c1c] pl-6 py-2 italic text-[#0f172a]" data-id="element-1102">
              Buying is a transaction. Building is a transfer.
            </blockquote>

            <p data-id="element-1103">
              And there's a second transfer that buying skips entirely. If you
              build a biosand filter alone in your garage on a Saturday while
              your teenage son plays video games inside, you have not
              transferred the system to him. You have just made yourself a
              single point of failure. If you build it with your son — asking
              him to wash the sand, asking him to stack the layers — you have
              transferred the capability. The work was never just water defense.
              It was <b data-id="element-1104">generational competence</b>.
            </p>
            <p data-id="element-1105">
              Pick one weekend in the next eight weeks and start. Don't wait for
              the rain. Don't wait for the news cycle. Spend your weekend the
              way Noah spent his.
            </p>
          </div>
        </article>

        {/* 4. 'You Might Also Like' Section */}
        <section className="space-y-8 pt-8 border-t border-gray-200" data-id="element-1106">
          <h2 className="text-3xl font-bold text-[#0f172a]" data-id="element-1107">
            You Might Also Like:
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8" data-id="element-1108">
            {/* Card 1 */}
            <div className="group cursor-pointer space-y-3" data-id="element-1109">
              <a href="https://trk.adsdomredir.com/click/2" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-1110">
                <img src="https://cdn.magicpatterns.com/uploads/tfBxRA9s5qgvhpKFp12h5o/unnamed_(7).jpg" alt="The Lost SuperFoods book" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-1111" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-1112">
                <a href="https://trk.adsdomredir.com/click/2" data-id="element-1113">
                  Superfoods That You Can Store Without Refrigeration for Years
                </a>
              </h3>
            </div>

            {/* Card 2 */}
            <div className="group cursor-pointer space-y-3" data-id="element-1114">
              <a href="https://trk.adsdomredir.com/click/3" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-1115">
                <img src="https://cdn.magicpatterns.com/uploads/aeW4oPiWoeR5Wzq6FuUxBZ/new5notext.png" alt="Vegetable garden" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-1116" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-1117">
                <a href="https://trk.adsdomredir.com/click/3" data-id="element-1118">
                  The Underground Energy Method They Don't Want You To Know
                  About
                </a>
              </h3>
            </div>

            {/* Card 3 */}
            <div className="group cursor-pointer space-y-3" data-id="element-1119">
              <a href="https://trk.adsdomredir.com/click/4" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-1120">
                <img src="https://cdn.magicpatterns.com/uploads/7ksskNxA12GTBLDP2Kydn9/img18.png" alt="Man holding tools" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-1121" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-1122">
                <a href="https://trk.adsdomredir.com/click/4" data-id="element-1123">
                  Nature-Inspired 3D Solar Sphere Generates Energy On Demand
                  (Watch Video)
                </a>
              </h3>
            </div>
          </div>

          <AuthorSignature data-id="element-1124" />
        </section>
      </main>

      {/* 5. Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12 py-12" data-id="element-1125">
        <div className="max-w-4xl mx-auto px-4 text-center space-y-6" data-id="element-1126">
          <div className="flex items-center justify-center gap-2 text-[#b91c1c]" data-id="element-1127">
            <div className="relative w-10 h-10" data-id="element-1128">
              <StarIcon className="w-10 h-10 fill-current text-[#002868]" data-id="element-1129" />
              <StarIcon className="w-4 h-4 fill-current text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-1130" />
            </div>
          </div>

          <div className="text-xs text-gray-500 space-y-4 max-w-2xl mx-auto leading-relaxed" data-id="element-1131">
            <p data-id="element-1132">
              © 2018-2026 PatriotsInformer Media, LLC | All Rights Reserved |
              63 N. Burritt - Buffalo, WY 82834 United States
            </p>
            <p className="space-x-2" data-id="element-1133">
              <button onClick={() => setActiveModal('terms')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-1134">
                Terms & Conditions
              </button>
              <span data-id="element-1135">|</span>
              <button onClick={() => setActiveModal('privacy')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-1136">
                Privacy Policy
              </button>
              <span data-id="element-1137">|</span>
              <button onClick={() => setActiveModal('contact')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-1138">
                Contact Us
              </button>
            </p>
            <p data-id="element-1139">
              No part of this report may be reproduced or placed on any
              electronic medium without written permission from the publisher.
              Information contained herein is obtained from sources believed to
              be reliable, but its accuracy cannot be guaranteed.
            </p>
          </div>
        </div>
      </footer>

      {/* Modals */}
      {activeModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-1140">
          <div className="absolute inset-0 bg-black/50" data-id="element-1141" />
          <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-1142">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between" data-id="element-1143">
              <h2 className="text-lg font-bold text-[#0f172a]" data-id="element-1144">
                {activeModal === 'terms' && 'Terms & Conditions'}
                {activeModal === 'privacy' && 'Privacy Policy'}
                {activeModal === 'contact' && 'Contact Us'}
              </h2>
              <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors" data-id="element-1145">
                <XIcon className="w-5 h-5 text-gray-500" data-id="element-1146" />
              </button>
            </div>

            <div className="px-6 py-6 text-sm text-[#334155] leading-relaxed space-y-4" data-id="element-1147">
              {activeModal === 'terms' && <>
                  <p className="font-semibold text-[#0f172a]" data-id="element-1148">
                    Last Updated: February 2026
                  </p>
                  <p data-id="element-1149">
                    Welcome to PatriotsInformer.com. By accessing or using our
                    website, you agree to be bound by these Terms & Conditions.
                  </p>
                </>}
              {activeModal === 'privacy' && <>
                  <p className="font-semibold text-[#0f172a]" data-id="element-1150">
                    Last Updated: February 2026
                  </p>
                  <p data-id="element-1151">
                    PatriotsInformer Media, LLC is committed to protecting your
                    privacy.
                  </p>
                </>}
              {activeModal === 'contact' && <>
                  <p data-id="element-1152">
                    We'd love to hear from you. Feel free to reach out using the
                    information below:
                  </p>
                  <div className="bg-[#f8f7f4] rounded-lg p-5 space-y-3 border border-gray-200" data-id="element-1153">
                    <h3 className="font-bold text-[#0f172a]" data-id="element-1154">
                      PatriotsInformer Media, LLC
                    </h3>
                    <p data-id="element-1155">
                      <span className="font-semibold text-[#0f172a]" data-id="element-1156">
                        Email:
                      </span>{' '}
                      <a href="mailto:support@patriotsinformer.com" className="text-[#002868] underline" data-id="element-1157">
                        support@patriotsinformer.com
                      </a>
                    </p>
                  </div>
                </>}
            </div>
          </div>
        </div>}
    </div>;
}